import React, { useState, useEffect, useCallback } from 'react';
import { timeRegistrationService } from '../services/TimeRegistrationService';
import { invoiceService } from '../services/InvoiceService';
import { useAuth } from '../contexts/AuthContext';
import CopilotProjectSummary from './CopilotProjectSummary';
import { buildFixedPriceInvoicingRowsByPerson } from './ReportInvoicing';
import * as XLSX from 'xlsx';
import './Report.css';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SHAREPOINT_SITE_URL = process.env.REACT_APP_SHAREPOINT_SITE_URL || 'https://igniagroup.sharepoint.com/sites/projects/';

// Helper functions for cookie management
const getCookie = (name: string): string | null => {
  const nameEQ = name + '=';
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(nameEQ) === 0) return cookie.substring(nameEQ.length);
  }
  return null;
};

const setCookie = (name: string, value: string, days: number = 365): void => {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = 'expires=' + date.toUTCString();
  document.cookie = `${name}=${value};${expires};path=/`;
};

type ReportViewMode = 'month' | 'week' | 'year';
type ReportDisplayMode = 'time' | 'invoicing' | 'salary';
type InvoiceGroupingMode = 'project' | 'activity';

// Key used for entries/rows without a project when tracking "Ta med i faktura" selections.
const NO_PROJECT_KEY = '__no_project__';

type ReportGroup = {
  rowKey: string;
  customer: string;
  activity: string;
  project?: string;
  hours: number;
  entries: any[];
  week?: number;
  weekYear?: number;
  periodLabel?: string;
};

type InvoicingCustomerRow = {
  rowKey: string;
  customer: string;
  description?: string;
  hours: number;
  amount: number;
  rateLabel: string;
};

const getIsoWeekInfo = (dateString: string) => {
  const date = new Date(`${dateString}T00:00:00`);
  const target = new Date(date);
  const dayNumber = (target.getDay() + 6) % 7;

  target.setDate(target.getDate() - dayNumber + 3);

  const weekYear = target.getFullYear();
  const firstThursday = new Date(weekYear, 0, 4);
  const firstThursdayDayNumber = (firstThursday.getDay() + 6) % 7;

  firstThursday.setDate(firstThursday.getDate() - firstThursdayDayNumber + 3);

  const week = 1 + Math.round((target.getTime() - firstThursday.getTime()) / 604800000);

  return {
    week,
    weekYear,
    label: `Vecka ${week}`
  };
};

const groupEntries = (entries: any[], viewMode: ReportViewMode): ReportGroup[] => {
  const grouped: Record<string, ReportGroup> = {};

  entries.forEach((entry: any) => {
    const customer = entry.Customer?.Title || 'Okänd kund';
    const activity = entry.Activity?.Title || 'Okänd aktivitet';
    const project = entry.Project?.Title;
    const projectPart = project ? `-${project}` : '';

    if (viewMode === 'week') {
      const { week, weekYear, label } = getIsoWeekInfo(entry.Date);
      const key = `${weekYear}-W${week}-${customer}${projectPart}`;

      if (!grouped[key]) {
        grouped[key] = {
          rowKey: key,
          customer,
          activity,
          project,
          hours: 0,
          entries: [],
          week,
          weekYear,
          periodLabel: label
        };
      }

      grouped[key].hours += entry.Hours;
      grouped[key].entries.push(entry);
      return;
    }

    const key = `${customer}${projectPart}`;

    if (!grouped[key]) {
      grouped[key] = {
        rowKey: key,
        customer,
        activity,
        project,
        hours: 0,
        entries: []
      };
    }

    grouped[key].hours += entry.Hours;
    grouped[key].entries.push(entry);
  });

  return Object.values(grouped).sort((a, b) => {
    if (viewMode === 'week') {
      return (
        (a.weekYear || 0) - (b.weekYear || 0) ||
        (a.week || 0) - (b.week || 0) ||
        a.customer.localeCompare(b.customer) ||
        (a.project || '').localeCompare(b.project || '') ||
        a.activity.localeCompare(b.activity)
      );
    }

    return (
      a.customer.localeCompare(b.customer) ||
      (a.project || '').localeCompare(b.project || '') ||
      a.activity.localeCompare(b.activity)
    );
  });
};

const Report: React.FC = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [allEntries, setAllEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<string>('all');
  const [selectedPerson, setSelectedPerson] = useState<string>(() => getCookie('selectedPerson') || '');
  const [selectedActivity, setSelectedActivity] = useState<string>(() => getCookie('selectedActivity') || 'all');
  const [viewMode, setViewMode] = useState<ReportViewMode>('month');
  const [reportMode, setReportMode] = useState<ReportDisplayMode>('time');
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [activityPrices, setActivityPrices] = useState<Record<string, number>>({});
  const [customerSuppliers, setCustomerSuppliers] = useState<Record<string, string>>({});
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [projectMonthPrices, setProjectMonthPrices] = useState<Record<number, { title: string; monthPrice: number }>>({});
  const [exportingInvoice, setExportingInvoice] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showCreateInvoiceDialog, setShowCreateInvoiceDialog] = useState(false);
  const [invoiceGroupingMode, setInvoiceGroupingMode] = useState<InvoiceGroupingMode>('project');
  const [exportingAllMonths, setExportingAllMonths] = useState(false);
  const [onlyIgniaDeliveries, setOnlyIgniaDeliveries] = useState(false);
  const [userType, setUserType] = useState<'Ägare' | 'Medarbetare' | 'Extern' | null>(null);
  const [employees, setEmployees] = useState<Array<{ id: number; name: string; lon: number }>>([]);
  // Projects checked under "Ta med i faktura"; empty means all projects are included.
  const [selectedInvoiceProjects, setSelectedInvoiceProjects] = useState<Set<string>>(new Set());
  // Lön beräknas alltid på föregående månads timmar, oavsett vilken månad som visas i navigeringen.
  const [salaryEntries, setSalaryEntries] = useState<any[]>([]);
  const { getAccessToken } = useAuth();

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const parseNumericValue = (value: unknown): number => {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }
    if (typeof value === 'string') {
      const normalized = value.replace(',', '.').trim();
      const parsed = parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  };

  const loadReportData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setError('Kunde inte få åtkomsttoken');
        return;
      }

      // Get current user info
      const user = await timeRegistrationService.getCurrentUserInfo(accessToken);
      setCurrentUser(user);

      // /Lists/Users is the source of truth for owner/member/external role, controlling
      // invoicing controls, the "Fakturerbart" column, and whether time is restricted to own entries.
      try {
        const type = await timeRegistrationService.getUserType(user.Id, accessToken);
        setUserType(type);
      } catch (userTypeErr) {
        console.warn('Could not verify user type from /Lists/Users:', userTypeErr);
        setUserType(null);
      }
      
      // Set selected person from cookie or default to current user on first load
      if (!selectedPerson || selectedPerson === '') {
        setSelectedPerson(user.Title);
        setCookie('selectedPerson', user.Title);
      }
      
      // Load all entries
      const entries = viewMode === 'year'
        ? await timeRegistrationService.getAllTimeEntriesForYear(year, accessToken)
        : await timeRegistrationService.getAllTimeEntries(year, month, accessToken);

      // Load hourly rates used for invoicing view (Activity_Customer list)
      try {
        setLoadingPrices(true);
        const sharePointUrl = process.env.REACT_APP_SHAREPOINT_SITE_URL || 'https://igniagroup.sharepoint.com/sites/projects';
        const siteRelPath = new URL(sharePointUrl).pathname.replace(/\/$/, '');
        const priceResponse = await fetch(
          `${sharePointUrl}/_api/web/GetList('${siteRelPath}/Lists/Activity_Customer')/items?$select=Activity/Title,Customer/Title,Price&$expand=Activity,Customer&$top=5000`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Accept': 'application/json'
            }
          }
        );

        if (priceResponse.ok) {
          const priceData = await priceResponse.json();
          const prices: Record<string, number> = {};

          if (Array.isArray(priceData.value)) {
            priceData.value.forEach((item: any) => {
              const activity = item.Activity?.Title;
              const customer = item.Customer?.Title;
              const price = parseFloat(item.Price);

              if (activity && customer && !Number.isNaN(price)) {
                prices[`${activity}|${customer}`] = price;
              }
            });
          }

          setActivityPrices(prices);
        } else {
          setActivityPrices({});
        }
      } catch (priceErr) {
        console.warn('Could not load activity prices:', priceErr);
        setActivityPrices({});
      } finally {
        setLoadingPrices(false);
      }

      // Load customer supplier mapping used by the "Bara Ignia-leveranser" filter.
      try {
        const sharePointUrl = process.env.REACT_APP_SHAREPOINT_SITE_URL || 'https://igniagroup.sharepoint.com/sites/projects';
        const siteRelPath = new URL(sharePointUrl).pathname.replace(/\/$/, '');
        const customerResponse = await fetch(
          `${sharePointUrl}/_api/web/GetList('${siteRelPath}/Lists/Customer')/items?$select=Title,Supplier/Title&$expand=Supplier&$top=5000`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Accept': 'application/json'
            }
          }
        );

        if (customerResponse.ok) {
          const customerData = await customerResponse.json();
          const suppliersByCustomer: Record<string, string> = {};

          if (Array.isArray(customerData.value)) {
            customerData.value.forEach((item: any) => {
              const customerTitle = item?.Title;
              const supplierTitle = item?.Supplier?.Title;
              if (customerTitle && supplierTitle) {
                suppliersByCustomer[customerTitle] = supplierTitle;
              }
            });
          }

          setCustomerSuppliers(suppliersByCustomer);
        } else {
          setCustomerSuppliers({});
        }
      } catch (customerErr) {
        console.warn('Could not load customer suppliers:', customerErr);
        setCustomerSuppliers({});
      }

      // Load project month prices used for invoicing fixed-price rows.
      try {
        const sharePointUrl = process.env.REACT_APP_SHAREPOINT_SITE_URL || 'https://igniagroup.sharepoint.com/sites/projects';
        const siteRelPath = new URL(sharePointUrl).pathname.replace(/\/$/, '');
        const monthPriceResponse = await fetch(
          `${sharePointUrl}/_api/web/GetList('${siteRelPath}/Lists/Projects')/items?$select=Id,Title,Monthprice&$top=5000`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Accept': 'application/json'
            }
          }
        );

        if (monthPriceResponse.ok) {
          const monthPriceData = await monthPriceResponse.json();
          const monthPrices: Record<number, { title: string; monthPrice: number }> = {};

          if (Array.isArray(monthPriceData.value)) {
            monthPriceData.value.forEach((item: any) => {
              const projectId = typeof item.Id === 'number' ? item.Id : parseInt(item.Id, 10);
              const monthPrice = parseNumericValue(item.Monthprice);
              if (Number.isFinite(projectId) && monthPrice > 0) {
                monthPrices[projectId] = {
                  title: item.Title || `Projekt ${projectId}`,
                  monthPrice
                };
              }
            });
          }

          setProjectMonthPrices(monthPrices);
        } else {
          setProjectMonthPrices({});
        }
      } catch (monthPriceErr) {
        console.warn('Could not load project month prices:', monthPriceErr);
        setProjectMonthPrices({});
      }
      
      // Load employees from the Employees list (Employee person field), used for the Lön view.
      try {
        const sharePointUrl = process.env.REACT_APP_SHAREPOINT_SITE_URL || 'https://igniagroup.sharepoint.com/sites/projects';
        const siteRelPath = new URL(sharePointUrl).pathname.replace(/\/$/, '');
        const employeesResponse = await fetch(
          `${sharePointUrl}/_api/web/GetList('${siteRelPath}/Lists/Employees')/items?$select=Id,Employee/Id,Employee/Title,L_x00f6_n&$expand=Employee&$top=5000`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Accept': 'application/json'
            }
          }
        );

        if (employeesResponse.ok) {
          const employeesData = await employeesResponse.json();
          const employeeList: Array<{ id: number; name: string; lon: number }> = [];

          if (Array.isArray(employeesData.value)) {
            employeesData.value.forEach((item: any) => {
              const id = item.Employee?.Id;
              const name = item.Employee?.Title;
              if (typeof id === 'number' && name) {
                employeeList.push({ id, name, lon: parseNumericValue(item.L_x00f6_n) });
              }
            });
          }

          setEmployees(employeeList);
        } else {
          setEmployees([]);
        }
      } catch (employeesErr) {
        console.warn('Could not load employees:', employeesErr);
        setEmployees([]);
      }

      // Store all entries for detail view
      setAllEntries(entries);
    } catch (err) {
      console.error('Error loading report data:', err);
      setError(err instanceof Error ? err.message : 'Kunde inte ladda rapportdata');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, viewMode, getAccessToken]);

  // /Lists/Users Usertype ('Ägare'/'Medarbetare'/'Extern') drives all role-based visibility below.
  const isOwnerGroupMember = userType === 'Ägare';
  const isExternalUser = userType === 'Extern';

  useEffect(() => {
    if (!isOwnerGroupMember && reportMode !== 'time') {
      setReportMode('time');
    }
  }, [isOwnerGroupMember, reportMode]);

  // Non-owners (Medarbetare/Extern) may only see their own time in the overview.
  const restrictToOwnTime = !isOwnerGroupMember;

  useEffect(() => {
    if (isExternalUser && selectedCustomer !== 'all') {
      setSelectedCustomer('all');
    }
  }, [isExternalUser, selectedCustomer]);

  useEffect(() => {
    if (restrictToOwnTime && currentUser?.Title && selectedPerson !== currentUser.Title) {
      setSelectedPerson(currentUser.Title);
    }
  }, [restrictToOwnTime, currentUser, selectedPerson]);

  useEffect(() => {
    loadReportData();
  }, [loadReportData]);

  // Save selected person to cookie when it changes
  useEffect(() => {
    setCookie('selectedPerson', selectedPerson);
  }, [selectedPerson]);

  // Save selected activity to cookie when it changes
  useEffect(() => {
    setCookie('selectedActivity', selectedActivity);
  }, [selectedActivity]);

  useEffect(() => {
    setExpandedRows(new Set());
  }, [currentDate, selectedCustomer, selectedPerson, selectedActivity, viewMode]);

  useEffect(() => {
    setSelectedInvoiceProjects(new Set());
  }, [selectedCustomer]);

  // Lön-fliken ska visa och räkna på föregående månads fakturerbara timmar, inte den valda/visade månaden.
  useEffect(() => {
    if (reportMode !== 'salary') {
      return;
    }

    let cancelled = false;

    const loadSalaryEntries = async () => {
      try {
        const accessToken = await getAccessToken();
        if (!accessToken) {
          return;
        }

        const prevMonthDate = new Date(year, month - 1, 1);
        const entries = await timeRegistrationService.getAllTimeEntries(
          prevMonthDate.getFullYear(),
          prevMonthDate.getMonth(),
          accessToken
        );

        if (!cancelled) {
          setSalaryEntries(entries);
        }
      } catch (err) {
        console.error('Error loading salary report data:', err);
        if (!cancelled) {
          setSalaryEntries([]);
        }
      }
    };

    loadSalaryEntries();

    return () => {
      cancelled = true;
    };
  }, [reportMode, year, month, getAccessToken]);

  useEffect(() => {
    if (reportMode !== 'invoicing' || !onlyIgniaDeliveries || selectedCustomer === 'all') {
      return;
    }

    const supplier = customerSuppliers[selectedCustomer];
    const isIgniaSupplier = typeof supplier === 'string' && supplier.trim().toLowerCase() === 'ignia';
    if (!isIgniaSupplier) {
      setSelectedCustomer('all');
    }
  }, [reportMode, onlyIgniaDeliveries, selectedCustomer, customerSuppliers]);

  const navigateMonth = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (viewMode === 'year') {
      newDate.setFullYear(newDate.getFullYear() + (direction === 'prev' ? -1 : 1));
    } else if (direction === 'prev') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const formatMonthYear = (date: Date) => {
    if (viewMode === 'year') {
      return date.toLocaleDateString('sv-SE', { year: 'numeric' });
    }
    const label = date.toLocaleDateString('sv-SE', { 
      month: 'long', 
      year: 'numeric' 
    });
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  const toggleRowExpand = (key: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedRows(newExpanded);
  };

  const getInvoiceProjectKey = (project?: string) => project || NO_PROJECT_KEY;

  const toggleInvoiceProjectSelection = (project?: string) => {
    const key = getInvoiceProjectKey(project);
    setSelectedInvoiceProjects(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleToggleInvoiced = async (item: any) => {
    try {
      setError(null);
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setError('Kunde inte få åtkomsttoken');
        return;
      }

      // Get all entries for this customer/project combination
      const entriesToUpdate = item.entries || [];
      
      if (entriesToUpdate.length === 0) {
        setError('Inga tidsregistreringar att uppdatera');
        return;
      }

      // Toggle the IsInvoiced status
      const newIsInvoiced = !entriesToUpdate[0].IsInvoiced;
      
      // Update all entries for this group
      await timeRegistrationService.updateTimeEntriesInvoiceStatus(
        entriesToUpdate,
        newIsInvoiced,
        accessToken
      );

      // Reload the report data
      await loadReportData();
      setSuccessMessage(`Uppdaterad fakturastatus för ${item.entries.length} tidsregistrering(ar)`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error toggling invoiced status:', err);
      setError(err instanceof Error ? err.message : 'Kunde inte uppdatera fakturastatus');
    }
  };

  // Bara ägargruppen får ändra Fakturerbart. Radnivå, inte gruppnivå — påverkar bara denna enskilda tidpost.
  const handleToggleEntryBillable = async (entry: any) => {
    if (!isOwnerGroupMember) return;
    try {
      setError(null);
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setError('Kunde inte få åtkomsttoken');
        return;
      }

      const newBillable = !(entry.Billable !== false);

      await timeRegistrationService.updateTimeEntriesBillableStatus(
        [entry],
        newBillable,
        accessToken
      );

      await loadReportData();
      setSuccessMessage('Uppdaterad fakturerbart-status');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error toggling billable status:', err);
      setError(err instanceof Error ? err.message : 'Kunde inte uppdatera fakturerbart-status');
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const formatCurrency = (amount: number) => (
    new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      maximumFractionDigits: 0
    }).format(amount)
  );

  const isIgniaSupplierCustomer = (customerTitle?: string): boolean => {
    if (!customerTitle) {
      return false;
    }

    const supplier = customerSuppliers[customerTitle];
    return typeof supplier === 'string' && supplier.trim().toLowerCase() === 'ignia';
  };

  // Extract unique customers and persons for filter dropdowns
  const allUniqueCustomers = Array.from(new Set(
    allEntries.map(entry => entry.Customer?.Title).filter(Boolean)
  )).sort() as string[];
  const uniqueCustomers = reportMode === 'invoicing' && onlyIgniaDeliveries
    ? allUniqueCustomers.filter((customer) => isIgniaSupplierCustomer(customer))
    : allUniqueCustomers;
  const uniquePersons = Array.from(new Set(
    allEntries.map(entry => entry.Person?.Title).filter(Boolean)
  )).sort() as string[];
  const uniqueActivities = Array.from(new Set(
    allEntries.map(entry => entry.Activity?.Title).filter(Boolean)
  )).sort() as string[];

  let filteredEntries = selectedCustomer === 'all'
    ? allEntries
    : allEntries.filter(entry => entry.Customer?.Title === selectedCustomer);

  if (selectedActivity !== 'all') {
    filteredEntries = filteredEntries.filter(entry => entry.Activity?.Title === selectedActivity);
  }

  // Lön-fliken visar alla anställda samtidigt och ska inte begränsas av Person-filtret.
  if (selectedPerson !== 'all' && reportMode !== 'salary') {
    filteredEntries = filteredEntries.filter(entry => entry.Person?.Title === selectedPerson);
  }

  if (reportMode === 'invoicing' && onlyIgniaDeliveries) {
    filteredEntries = filteredEntries.filter(entry => isIgniaSupplierCustomer(entry.Customer?.Title));
  }

  // Icke-fakturerbara tidposter ska aldrig räknas med i faktureringsunderlaget.
  if (reportMode === 'invoicing') {
    filteredEntries = filteredEntries.filter(entry => entry.Billable !== false);
  }

  const hasMonthPriceProject = (entry: any): boolean => {
    const projectId = entry.Project?.Id;
    if (typeof projectId !== 'number') {
      return false;
    }
    return (projectMonthPrices[projectId]?.monthPrice || 0) > 0;
  };

  const filteredData = groupEntries(filteredEntries, viewMode);
  const isGroupByCustomer = selectedCustomer === 'all' && viewMode === 'month';
  const customerSections = isGroupByCustomer
    ? filteredData.reduce((sections, item) => {
        const existing = sections.find(s => s.customer === item.customer);
        if (existing) {
          existing.items.push(item);
          existing.totalHours += item.hours;
        } else {
          sections.push({ customer: item.customer, items: [item], totalHours: item.hours });
        }
        return sections;
      }, [] as Array<{ customer: string; items: ReportGroup[]; totalHours: number }>)
    : [];
  const yearlySections = viewMode === 'year'
    ? filteredData.reduce((sections, item) => {
        const existing = sections.find(section => section.customer === item.customer);
        if (existing) {
          existing.items.push(item);
          existing.totalHours += item.hours;
        } else {
          sections.push({ customer: item.customer, items: [item], totalHours: item.hours });
        }
        return sections;
      }, [] as Array<{ customer: string; items: ReportGroup[]; totalHours: number }>)
        .sort((first, second) => second.totalHours - first.totalHours || first.customer.localeCompare(second.customer))
    : [];
  const yearlyTotalHours = filteredEntries.reduce((sum, entry) => sum + entry.Hours, 0);
  const formatPercentage = (value: number, total: number) => (
    total > 0 ? `${((value / total) * 100).toFixed(1)}%` : '0.0%'
  );
  const invoicingSections = reportMode === 'invoicing'
    ? (() => {
        const personMap = new Map<string, Map<string, { hours: number; amount: number; rates: Set<number> }>>();
        const fixedPriceRowsByPerson = buildFixedPriceInvoicingRowsByPerson(
          filteredEntries,
          projectMonthPrices,
          activityPrices,
          selectedCustomer === 'all'
        );

        filteredEntries.forEach((entry: any) => {
          const person = entry.Person?.Title || 'Okänd person';
          const customer = entry.Customer?.Title || 'Okänd kund';
          const activity = entry.Activity?.Title || 'Okänd aktivitet';
          const hours = entry.Hours || 0;
          if (hasMonthPriceProject(entry)) {
            if (!personMap.has(person)) {
              personMap.set(person, new Map());
            }
            return;
          }

          const priceKey = `${activity}|${customer}`;
          const rate = activityPrices[priceKey] ?? 0;
          const amount = hours * rate;

          if (!personMap.has(person)) {
            personMap.set(person, new Map());
          }

          const customerMap = personMap.get(person)!;
          if (!customerMap.has(customer)) {
            customerMap.set(customer, { hours: 0, amount: 0, rates: new Set<number>() });
          }

          const customerRow = customerMap.get(customer)!;
          customerRow.hours += hours;
          customerRow.amount += amount;
          if (rate > 0) {
            customerRow.rates.add(rate);
          }
        });

        return Array.from(personMap.entries())
          .map(([person, customerMap]) => {
            const hourlyRows: InvoicingCustomerRow[] = Array.from(customerMap.entries())
              .map(([customer, values]) => {
                const rates = Array.from(values.rates);
                const avgRate = values.hours > 0 ? values.amount / values.hours : 0;
                const rateLabel = rates.length === 0
                  ? '0 kr'
                  : rates.length === 1
                    ? `${rates[0].toFixed(0)} kr`
                    : `${avgRate.toFixed(0)} kr (snitt)`;

                return {
                  rowKey: `hourly-${customer}`,
                  customer,
                  hours: values.hours,
                  amount: values.amount,
                  rateLabel
                };
              })
              .sort((a, b) => a.customer.localeCompare(b.customer));

            const fixedPriceRows: InvoicingCustomerRow[] = (fixedPriceRowsByPerson.get(person) || []).map((row) => ({
              rowKey: row.rowKey,
              customer: row.customer,
              description: row.project,
              hours: row.hours,
              amount: row.amount,
              rateLabel: row.rateLabel
            }));
            const rows = [...hourlyRows.filter((row) => row.amount > 0), ...fixedPriceRows];

            const totalHoursByPerson = rows.reduce((sum, row) => sum + row.hours, 0);
            const totalAmountByPerson = rows.reduce((sum, row) => sum + row.amount, 0);

            return {
              person,
              rows,
              totalHours: totalHoursByPerson,
              totalAmount: totalAmountByPerson
            };
          })
          .sort((a, b) => a.person.localeCompare(b.person))
          .filter(section => section.rows.length > 0);
      })()
    : [];
  const customerMonthPriceRows: InvoicingCustomerRow[] =
    reportMode === 'invoicing' && selectedCustomer !== 'all'
      ? (() => {
          const projectsWithTime = new Set<number>();
          const rows: InvoicingCustomerRow[] = [];

          allEntries
            .filter((entry) =>
              entry.Customer?.Title === selectedCustomer &&
              (!onlyIgniaDeliveries || isIgniaSupplierCustomer(entry.Customer?.Title))
            )
            .forEach((entry) => {
              const projectId = entry.Project?.Id;
              if (typeof projectId !== 'number' || projectsWithTime.has(projectId)) return;

              const monthPriceInfo = projectMonthPrices[projectId];
              if (!monthPriceInfo || monthPriceInfo.monthPrice <= 0) return;

              projectsWithTime.add(projectId);
              rows.push({
                rowKey: `monthprice-${projectId}`,
                customer: selectedCustomer,
                description: monthPriceInfo.title,
                hours: 0,
                amount: monthPriceInfo.monthPrice,
                rateLabel: '-'
              });
            });

          return rows.sort((first, second) =>
            (first.description || '').localeCompare(second.description || '', 'sv')
          );
        })()
      : [];
  const weeklySections = viewMode === 'week'
    ? filteredData.reduce((sections, item) => {
        const sectionKey = `${item.weekYear}-${item.week}`;
        const existingSection = sections.find(section => section.key === sectionKey);

        if (existingSection) {
          existingSection.items.push(item);
          existingSection.totalHours += item.hours;
        } else {
          sections.push({
            key: sectionKey,
            title: item.periodLabel || 'Vecka',
            items: [item],
            totalHours: item.hours
          });
        }

        return sections;
      }, [] as Array<{ key: string; title: string; items: ReportGroup[]; totalHours: number }>)
    : [];
  const totalHours = filteredEntries.reduce((sum, entry) => sum + entry.Hours, 0);
  const invoicingTotalAmount =
    invoicingSections.reduce((sum, section) => sum + section.totalAmount, 0) +
    customerMonthPriceRows.reduce((sum, row) => sum + row.amount, 0);

  // Lön: (fakturerbara timmar - 40) x 120 kr, bara timmar över 40, + den anställdes grundlön (fältet "Lön" i Employees).
  // Beräkningen görs alltid på föregående månads timmar (salaryEntries), inte den valda/visade månaden.
  let salaryFilteredEntries = selectedCustomer === 'all'
    ? salaryEntries
    : salaryEntries.filter(entry => entry.Customer?.Title === selectedCustomer);

  if (selectedActivity !== 'all') {
    salaryFilteredEntries = salaryFilteredEntries.filter(entry => entry.Activity?.Title === selectedActivity);
  }

  // Timmar med timpris 0 kr (Activity_Customer) räknas inte som fakturerbara för lönen,
  // förutom timmar på projekt med fast månadspris.
  const isSalaryBillableEntry = (entry: any): boolean => {
    if (entry.Billable === false) {
      return false;
    }
    if (hasMonthPriceProject(entry)) {
      return true;
    }
    const priceKey = `${entry.Activity?.Title || 'Okänd aktivitet'}|${entry.Customer?.Title || 'Okänd kund'}`;
    return (activityPrices[priceKey] ?? 0) > 0;
  };

  const salarySections = reportMode === 'salary'
    ? employees
        .map((employee) => {
          const employeeEntries = salaryFilteredEntries.filter((entry: any) => entry.Person?.Id === employee.id);
          const totalAllHoursForEmployee = employeeEntries
            .reduce((sum: number, entry: any) => sum + (entry.Hours || 0), 0);
          const totalHoursForEmployee = employeeEntries
            .filter(isSalaryBillableEntry)
            .reduce((sum: number, entry: any) => sum + (entry.Hours || 0), 0);
          const overtimeHoursForEmployee = Math.max(totalHoursForEmployee - 40, 0);

          return {
            employeeId: employee.id,
            name: employee.name,
            totalHours: totalHoursForEmployee,
            totalAllHours: totalAllHoursForEmployee,
            lon: employee.lon,
            amount: overtimeHoursForEmployee * 120 + employee.lon
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name, 'sv'))
    : [];
  const salaryTotalHours = salarySections.reduce((sum, section) => sum + section.totalHours, 0);
  const salaryTotalAmount = salarySections.reduce((sum, section) => sum + section.amount, 0);

  const handleExportClick = () => {
    setShowExportDialog(true);
  };

  const buildExcelFromData = (data: any[], total: number, filenameLabel: string) => {
    const excelData: any[] = [];
    const numCols = 8;

    data.forEach(item => {
      if (item.entries && item.entries.length > 0) {
        item.entries.forEach((entry: any) => {
          excelData.push({
            'Kund': item.customer,
            'Projekt': item.project || '',
            'Aktivitet': entry.Activity?.Title || item.activity,
            'Datum': formatDate(entry.Date),
            'Timmar': entry.Hours,
            'Beskrivning': entry.Title || '',
            'Längre kommentar': entry.Details || '',
            'Person': entry.Person?.Title || ''
          });
        });
      }
    });

    excelData.push({
      'Kund': '',
      'Projekt': '',
      'Aktivitet': 'TOTALT',
      'Datum': '',
      'Timmar': total,
      'Beskrivning': '',
      'Längre kommentar': '',
      'Person': ''
    });

    const worksheet = XLSX.utils.json_to_sheet(excelData);

    const colWidths = [
      { wch: 20 }, // Kund
      { wch: 20 }, // Projekt
      { wch: 25 }, // Aktivitet
      { wch: 12 }, // Datum
      { wch: 10 }, // Timmar
      { wch: 40 }, // Beskrivning
      { wch: 40 }, // Längre kommentar
      { wch: 20 }  // Person
    ];
    worksheet['!cols'] = colWidths;

    worksheet['!autofilter'] = { ref: `A1:${XLSX.utils.encode_col(numCols - 1)}1` };

    const totalExcelRowNum = excelData.length + 1;
    for (let col = 0; col < numCols; col++) {
      const cellRef = XLSX.utils.encode_col(col) + totalExcelRowNum;
      if (worksheet[cellRef]) {
        worksheet[cellRef].s = {
          font: { bold: true },
          fill: { fgColor: { rgb: "E8E8E8" } },
          alignment: { horizontal: "left", vertical: "center" }
        };
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Rapport');

    const filename = `${filenameLabel}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  const exportToExcel = () => {
    setShowExportDialog(false);
    const monthName = currentDate.toLocaleDateString('sv-SE', { month: 'long' });
    const yearStr = currentDate.getFullYear();
    const customerName = selectedCustomer === 'all' ? 'Alla kunder' : selectedCustomer;
    const filenameLabel = `${customerName} ${monthName} ${yearStr}`;
    buildExcelFromData(filteredData, totalHours, filenameLabel);
  };

  const exportToExcelAllMonths = async () => {
    setShowExportDialog(false);
    setExportingAllMonths(true);
    setError(null);

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setError('Kunde inte få åtkomsttoken');
        return;
      }

      const allEntriesAll = await timeRegistrationService.getAllTimeEntriesAllMonths(accessToken);

      // Apply same filters as current view
      const grouped: { [key: string]: { customer: string; activity: string; project?: string; hours: number; entries: any[] } } = {};

      allEntriesAll.forEach((entry: any) => {
        const projectPart = entry.Project ? `-${entry.Project.Title}` : '';
        const key = `${entry.Customer.Title}${projectPart}`;

        if (!grouped[key]) {
          grouped[key] = {
            customer: entry.Customer.Title,
            activity: entry.Activity.Title,
            hours: 0,
            entries: []
          };
          if (entry.Project) {
            grouped[key].project = entry.Project.Title;
          }
        }
        grouped[key].hours += entry.Hours;
        grouped[key].entries.push(entry);
      });

      let allData = Object.values(grouped).sort((a, b) =>
        a.customer.localeCompare(b.customer) || a.activity.localeCompare(b.activity)
      );

      // Apply customer filter
      if (selectedCustomer !== 'all') {
        allData = allData.filter(item => item.customer === selectedCustomer);
      }

      // Apply activity filter
      if (selectedActivity !== 'all') {
        allData = allData.filter(item => item.activity === selectedActivity);
      }

      // Apply person filter
      if (selectedPerson !== 'all') {
        allData = allData.map(item => ({
          ...item,
          entries: item.entries.filter((entry: any) => entry.Person?.Title === selectedPerson),
          hours: item.entries
            .filter((entry: any) => entry.Person?.Title === selectedPerson)
            .reduce((sum: number, entry: any) => sum + entry.Hours, 0)
        })).filter(item => item.hours > 0);
      }

      const allTotal = allData.reduce((sum, item) => sum + item.hours, 0);
      const customerName = selectedCustomer === 'all' ? 'Alla kunder' : selectedCustomer;
      const filenameLabel = `${customerName} alla månader`;
      buildExcelFromData(allData, allTotal, filenameLabel);
    } catch (err) {
      console.error('Error exporting all months:', err);
      setError(err instanceof Error ? err.message : 'Kunde inte exportera alla månader');
    } finally {
      setExportingAllMonths(false);
    }
  };

  const openCreateInvoiceDialog = () => {
    if (filteredData.length === 0) {
      setError('Inga tidsregistreringar att exportera');
      return;
    }

    if (!selectedCustomer || selectedCustomer === 'all') {
      setError('Du måste välja en kund för att exportera till faktura');
      return;
    }

    setError(null);
    setShowCreateInvoiceDialog(true);
  };

  const exportToInvoices = async (groupingMode: InvoiceGroupingMode) => {
    if (filteredData.length === 0) {
      setError('Inga tidsregistreringar att exportera');
      return;
    }

    if (!selectedCustomer || selectedCustomer === 'all') {
      setError('Du måste välja en kund för att exportera till faktura');
      return;
    }

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setError('Kunde inte få åtkomsttoken');
        return;
      }

      // Fetch supplier information for the selected customer
      const sharePointUrl = process.env.REACT_APP_SHAREPOINT_SITE_URL || 'https://igniagroup.sharepoint.com/sites/projects';
      const siteRelPath = new URL(sharePointUrl).pathname.replace(/\/$/, '');
      const response = await fetch(
        `${sharePointUrl}/_api/web/GetList('${siteRelPath}/Lists/Customer')/items?$select=Title,Supplier/Title&$expand=Supplier&$top=1000`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Accept': 'application/json'
          }
        }
      );

      const data = await response.json();
      let supplierName = null;

      if (data.value && Array.isArray(data.value)) {
        const customer = data.value.find((item: any) => item.Title === selectedCustomer);
        if (customer && customer.Supplier && customer.Supplier.Title) {
          supplierName = customer.Supplier.Title;
        }
      }

      // If supplier is missing, show error and don't proceed
      if (!supplierName) {
        setError('Supplier saknas för denna kund. Vänligen lägg till en supplier i SharePoint.');
        return;
      }

      // Show confirmation dialog
      const confirmMessage = `Detta kommer att skapa en faktura i ${supplierName}s faktura.nu. Vill du fortsätta?`;
      if (!window.confirm(confirmMessage)) {
        return;
      }

      // Bara fakturerbara tidposter får komma med på fakturan, oavsett vilken flik man står på.
      const billableEntries = filteredEntries.filter((entry: any) => entry.Billable !== false);

      // If no projects are explicitly checked under "Ta med i faktura", include all projects.
      const invoiceEntries = selectedInvoiceProjects.size === 0
        ? billableEntries
        : billableEntries.filter((entry: any) => selectedInvoiceProjects.has(getInvoiceProjectKey(entry.Project?.Title)));

      if (invoiceEntries.length === 0) {
        setError('Inga fakturerbara tidsregistreringar för valda projekt att exportera');
        return;
      }

      setExportingInvoice(true);
      setError(null);
      setSuccessMessage(null);

      const result = await invoiceService.exportToInvoices(invoiceEntries, selectedCustomer, accessToken, {
        groupingMode,
        projectMonthPrices,
        activityPrices
      });

      if (result.failed > 0) {
        // Show error in error bar - make it human friendly
        let errorDetails = result.errors.map(err => {
          let cleanError = err;
          try {
            // Try to find and parse JSON in the error string
            const jsonStart = err.indexOf('{"');
            if (jsonStart !== -1) {
              // Find the matching closing brace by counting parentheses
              let braceCount = 0;
              let jsonEnd = -1;
              for (let i = jsonStart; i < err.length; i++) {
                if (err[i] === '{') braceCount++;
                if (err[i] === '}') braceCount--;
                if (braceCount === 0) {
                  jsonEnd = i + 1;
                  break;
                }
              }
              // Extract and parse the JSON
              if (jsonEnd !== -1) {
                const jsonStr = err.substring(jsonStart, jsonEnd);
                const jsonObj = JSON.parse(jsonStr);
                if (jsonObj.error) {
                  cleanError = jsonObj.error;
                }
              }
            }
          } catch (e) {
            // Keep original error if parsing fails
          }
          return cleanError;
        });
        
        let message = `Kunde inte skapa alla fakturor`;
        if (errorDetails.length > 0) {
          message += ':\n' + errorDetails.join('\n');
        }
        setError(message);
      } else {
        // Show success message text
        setSuccessMessage(`Faktura skapad. 🎵 Pengar rullar in, det går bra nu! 🎵`);
      }
    } catch (err) {
      // Show error in error bar
      const errorMessage = err instanceof Error ? err.message : 'Okänt fel';
      setError(`Kunde inte exportera till faktura: ${errorMessage}`);
    } finally {
      setExportingInvoice(false);
    }
  };

  const handleCreateInvoiceConfirm = async () => {
    setShowCreateInvoiceDialog(false);
    await exportToInvoices(invoiceGroupingMode);
  };

  const renderActivitySummary = (entries: any[]) => {
    const activityTotals: Record<string, number> = {};
    entries.forEach((entry: any) => {
      const activityName = entry.Activity?.Title || 'Okänd aktivitet';
      activityTotals[activityName] = (activityTotals[activityName] || 0) + entry.Hours;
    });

    return (
      <table className="details-table activity-summary-table">
        <thead>
          <tr>
            <th>Aktivitet</th>
            <th>Timmar</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(activityTotals)
            .sort(([first], [second]) => first.localeCompare(second))
            .map(([activityName, hours]) => (
              <tr key={activityName}>
                <td>{activityName}</td>
                <td>{hours.toFixed(1)}h ({formatPercentage(hours, entries.reduce((sum, entry) => sum + entry.Hours, 0))})</td>
              </tr>
            ))}
        </tbody>
      </table>
    );
  };

  const renderReportRows = (
    items: ReportGroup[],
    showPeriodInRow: boolean,
    hideCustomerPrefix: boolean = false,
    summaryOnly: boolean = false,
    percentageTotalHours?: number,
    showInvoiceSelectionColumn: boolean = false
  ) => (
    <>
      {items.map((item, index) => {
        const rowKey = item.rowKey;
        const isExpanded = expandedRows.has(rowKey);
        const baseLabel = hideCustomerPrefix
          ? (item.project || item.customer)
          : (item.project ? `${item.customer} - ${item.project}` : item.customer);
        const displayLabel = showPeriodInRow && item.periodLabel ? `${item.periodLabel} - ${baseLabel}` : baseLabel;
        const isInvoiced = item.entries && item.entries.length > 0 ? item.entries[0].IsInvoiced : false;

        return (
          <React.Fragment key={`${rowKey}-${index}`}>
            <tr
              className="report-row clickable"
              onClick={() => toggleRowExpand(rowKey)}
              role="button"
              tabIndex={0}
              aria-expanded={isExpanded}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleRowExpand(rowKey);
                }
              }}
            >
              <td className="expand-cell">
                <span className="expand-icon">{isExpanded ? '−' : '+'}</span>
              </td>
              <td>{displayLabel}</td>
              <td className="left"><strong>{item.hours.toFixed(1)}h{percentageTotalHours !== undefined ? ` (${formatPercentage(item.hours, percentageTotalHours)})` : ''}</strong></td>
              {isOwnerGroupMember && !summaryOnly && (
                <td className="checkbox-cell" onClick={(e) => e.stopPropagation()}>
                  <input 
                    type="checkbox"
                    checked={isInvoiced}
                    onChange={() => handleToggleInvoiced(item)}
                    className="invoiced-checkbox"
                    title="Markera som fakturerad"
                  />
                </td>
              )}
              {showInvoiceSelectionColumn && !summaryOnly && (
                <td className="checkbox-cell" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedInvoiceProjects.has(getInvoiceProjectKey(item.project))}
                    onChange={() => toggleInvoiceProjectSelection(item.project)}
                    className="invoiced-checkbox"
                    title="Ta med i faktura"
                  />
                </td>
              )}
            </tr>
            {isExpanded && item.entries && (
              <tr className="details-row">
                <td colSpan={summaryOnly ? 4 : (isOwnerGroupMember ? (showInvoiceSelectionColumn ? 6 : 5) : (showInvoiceSelectionColumn ? 5 : 4))}>
                  <div className="details-container">
                    {summaryOnly ? renderActivitySummary(item.entries) : (() => {
                      const groupedByActivity: { [key: string]: any[] } = {};
                      item.entries.forEach((entry: any) => {
                        const activityTitle = entry.Activity?.Title || 'Okänd aktivitet';
                        if (!groupedByActivity[activityTitle]) {
                          groupedByActivity[activityTitle] = [];
                        }
                        groupedByActivity[activityTitle].push(entry);
                      });

                      return (
                        <>
                          {Object.entries(groupedByActivity).map(([activityName, entries]) => {
                            const activityTotal = entries.reduce((sum: number, entry: any) => sum + entry.Hours, 0);
                            return (
                              <div key={activityName} className="activity-section">
                                <h4 className="activity-header">{activityName} <span className="activity-hours">({activityTotal.toFixed(1)}h)</span></h4>
                                <table className="details-table">
                                  <thead>
                                    <tr>
                                      <th>Datum</th>
                                      <th>Timmar</th>
                                      <th>Beskrivning</th>
                                      <th>Person</th>
                                      {isOwnerGroupMember && <th>Fakturerbart</th>}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {entries.map((entry: any, entryIndex: number) => (
                                      <tr key={entryIndex} className="detail-entry">
                                        <td>{formatDate(entry.Date)}</td>
                                        <td>{entry.Hours}h</td>
                                        <td className="description-cell">{entry.Title || '—'}</td>
                                        <td className="person-cell">{entry.Person?.Title || '—'}</td>
                                        {isOwnerGroupMember && (
                                          <td className="checkbox-cell">
                                            <input
                                              type="checkbox"
                                              checked={entry.Billable !== false}
                                              onChange={() => handleToggleEntryBillable(entry)}
                                              className="invoiced-checkbox"
                                              title="Fakturerbart"
                                            />
                                          </td>
                                        )}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            );
                          })}
                        </>
                      );
                    })()}
                  </div>
                </td>
              </tr>
            )}
          </React.Fragment>
        );
      })}
    </>
  );

  return (
    <div className="report-view">
      <div className="report-header">
        <div className="report-filters">
          {!isExternalUser && (
            <>
              <label htmlFor="customer-filter" className="filter-label">Kund:</label>
              <select 
                id="customer-filter"
                className="customer-filter"
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
                disabled={loading}
              >
                <option value="all">Alla kunder</option>
                {uniqueCustomers.map(customer => (
                  <option key={customer} value={customer}>{customer}</option>
                ))}
              </select>
            </>
          )}

          <label htmlFor="person-filter" className="filter-label">Person:</label>
          {restrictToOwnTime ? (
            <span className="customer-filter" title="Du ser endast din egen tid">
              {currentUser?.Title || ''}
            </span>
          ) : (
            <select 
              id="person-filter"
              className="customer-filter"
              value={selectedPerson}
              onChange={(e) => setSelectedPerson(e.target.value)}
              disabled={loading}
            >
              <option value="all">Alla personer</option>
              {uniquePersons.map(person => (
                <option key={person} value={person}>
                  {person === currentUser?.Title ? `${person} (du)` : person}
                </option>
              ))}
            </select>
          )}

          <label htmlFor="activity-filter" className="filter-label">Aktivitet:</label>
          <select 
            id="activity-filter"
            className="customer-filter"
            value={selectedActivity}
            onChange={(e) => setSelectedActivity(e.target.value)}
            disabled={loading}
          >
            <option value="all">Alla aktiviteter</option>
            {uniqueActivities.map(activity => (
              <option key={activity} value={activity}>{activity}</option>
            ))}
          </select>

          <div className="view-toggle" role="group" aria-label="Välj uppdelning">
            <button
              type="button"
              className={`view-toggle-button ${viewMode === 'month' ? 'active' : ''}`}
              onClick={() => setViewMode('month')}
              disabled={loading}
            >
              Månad
            </button>
            <button
              type="button"
              className={`view-toggle-button ${viewMode === 'week' ? 'active' : ''}`}
              onClick={() => setViewMode('week')}
              disabled={loading || reportMode === 'invoicing' || reportMode === 'salary'}
            >
              Veckor
            </button>
            <button
              type="button"
              className={`view-toggle-button ${viewMode === 'year' ? 'active' : ''}`}
              onClick={() => {
                setViewMode('year');
                setReportMode('time');
              }}
              disabled={loading || reportMode === 'invoicing' || reportMode === 'salary'}
            >
              År
            </button>
          </div>

          {isOwnerGroupMember && (
            <div className="view-toggle" role="group" aria-label="Välj rapporttyp">
              <button
                type="button"
                className={`view-toggle-button ${reportMode === 'time' ? 'active' : ''}`}
                onClick={() => setReportMode('time')}
                disabled={loading}
              >
                Tid
              </button>
              <button
                type="button"
                className={`view-toggle-button ${reportMode === 'invoicing' ? 'active' : ''}`}
                onClick={() => setReportMode('invoicing')}
                disabled={loading}
              >
                Fakturering
              </button>
              <button
                type="button"
                className={`view-toggle-button ${reportMode === 'salary' ? 'active' : ''}`}
                onClick={() => setReportMode('salary')}
                disabled={loading}
              >
                Lön
              </button>
            </div>
          )}

          {isOwnerGroupMember && reportMode === 'invoicing' && (
            <label className="filter-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="checkbox"
                checked={onlyIgniaDeliveries}
                onChange={(e) => setOnlyIgniaDeliveries(e.target.checked)}
                disabled={loading}
              />
              Bara Ignia-leveranser
            </label>
          )}

          <button 
            onClick={handleExportClick}
            className="export-button"
            disabled={loading || exportingAllMonths}
          >
            {exportingAllMonths ? '⏳ Exporterar...' : '📊 Exportera till Excel'}
          </button>

          {isOwnerGroupMember && (
            <button 
              onClick={openCreateInvoiceDialog}
              className="export-button invoice-button"
              disabled={loading || exportingInvoice || !selectedCustomer || selectedCustomer === 'all'}
            >
              {exportingInvoice ? '⏳ Skapar fakturor...' : '💵 Skapa faktura'}
            </button>
          )}

          {successMessage && <span className="success-message">{successMessage}</span>}
        </div>

        {isOwnerGroupMember && reportMode === 'time' && selectedCustomer !== 'all' && (
          <CopilotProjectSummary
            key={`${selectedCustomer}-${viewMode}-${year}-${month}`}
            customer={selectedCustomer}
            period={formatMonthYear(currentDate)}
            entries={allEntries.filter((entry) => entry.Customer?.Title === selectedCustomer)}
          />
        )}

        <div className="month-navigation">
          <button 
            onClick={() => navigateMonth('prev')} 
            className="nav-button"
            disabled={loading}
          >
            ← Föregående
          </button>
          <h2 className="month-title">{formatMonthYear(currentDate)}</h2>
          <button 
            onClick={() => navigateMonth('next')} 
            className="nav-button"
            disabled={loading}
          >
            Nästa →
          </button>
        </div>

        {reportMode === 'salary' && (
          <div className="salary-period-hint" style={{ fontSize: '0.85em', opacity: 0.75, marginTop: '-8px', marginBottom: '8px' }}>
            Lön beräknas på föregående månads fakturerbara timmar: <strong>{formatMonthYear(new Date(year, month - 1, 1))}</strong>
          </div>
        )}

        {error && (
          <div className="error-bar">
            <span>{error}</span>
          </div>
        )}
        
        <div className="report-summary">
          <span className="total-hours">
            Totala timmar: <strong>{(reportMode === 'salary' ? salaryTotalHours : totalHours).toFixed(1)}h</strong>
          </span>
          {reportMode === 'invoicing' && (
            <span className="total-hours" style={{ marginLeft: '12px' }}>
              Totalt belopp: <strong>{formatCurrency(invoicingTotalAmount)}</strong>
            </span>
          )}
          {reportMode === 'salary' && (
            <span className="total-hours" style={{ marginLeft: '12px' }}>
              Totalt belopp: <strong>{formatCurrency(salaryTotalAmount)}</strong>
            </span>
          )}
          {reportMode === 'invoicing' && loadingPrices && <span className="loading-indicator">Läser in timpriser...</span>}
          {loading && <span className="loading-indicator">Läser in...</span>}
        </div>
      </div>

      {reportMode === 'invoicing' ? (
        <div className="report-weeks-container">
          {invoicingSections.length === 0 && customerMonthPriceRows.length === 0 && !loading ? (
            <div className="report-table-container">
              <table className="report-table">
                <tbody>
                  <tr>
                    <td colSpan={3} className="no-data">Inga tidsregistreringar för detta urval</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <>
              {invoicingSections.map((section) => (
                <section key={section.person} className="report-week-section">
                  <div className="report-week-header">
                    <h3>{section.person}</h3>
                    <span>{section.totalHours.toFixed(1)}h • {formatCurrency(section.totalAmount)}</span>
                  </div>
                  <div className="report-table-container">
                    <table className="report-table report-invoicing-table">
                      <thead>
                        <tr>
                          <th>Kund</th>
                          <th>Timmar x Timpris</th>
                          <th>Belopp</th>
                        </tr>
                      </thead>
                      <tbody>
                        {section.rows.map((row) => (
                          <tr key={`${section.person}-${row.rowKey}`} className="report-row">
                            <td>{row.description ? `${row.customer} - ${row.description}` : row.customer}</td>
                            <td>{row.hours.toFixed(1)}h x {row.rateLabel}</td>
                            <td className="left"><strong>{formatCurrency(row.amount)}</strong></td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td><strong>Totalt</strong></td>
                          <td><strong>{section.totalHours.toFixed(1)}h</strong></td>
                          <td className="left"><strong>{formatCurrency(section.totalAmount)}</strong></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </section>
              ))}

              {customerMonthPriceRows.length > 0 && (
                <section className="report-week-section">
                  <div className="report-week-header">
                    <h3>Fast månadspris · {selectedCustomer}</h3>
                    <span>{formatCurrency(customerMonthPriceRows.reduce((sum, row) => sum + row.amount, 0))}</span>
                  </div>
                  <div className="report-table-container">
                    <table className="report-table report-invoicing-table">
                      <thead>
                        <tr>
                          <th>Kund / projekt</th>
                          <th>Prisform</th>
                          <th>Belopp</th>
                        </tr>
                      </thead>
                      <tbody>
                        {customerMonthPriceRows.map((row) => (
                          <tr key={row.rowKey} className="report-row">
                            <td>{row.customer} - {row.description}</td>
                            <td>Fast månadspris</td>
                            <td className="left"><strong>{formatCurrency(row.amount)}</strong></td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td><strong>Totalt</strong></td>
                          <td></td>
                          <td className="left">
                            <strong>{formatCurrency(customerMonthPriceRows.reduce((sum, row) => sum + row.amount, 0))}</strong>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      ) : reportMode === 'salary' ? (
        <div className="report-table-container">
          <table className="report-table report-invoicing-table">
            <thead>
              <tr>
                <th>Anställd</th>
                <th>Totala fakturerbara timmar</th>
                <th>Lön</th>
                <th>
                  Summa
                  <span
                    className="info-icon"
                    title="Summa = (fakturerbara timmar - 40) x 120 kr + lön. Endast timmar utöver 40 ersätts med 120 kr/h."
                  >
                    ⓘ
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {salarySections.length === 0 && !loading ? (
                <tr>
                  <td colSpan={4} className="no-data">Inga anställda hittades</td>
                </tr>
              ) : (
                salarySections.map((section) => (
                  <tr key={section.employeeId} className="report-row">
                    <td>{section.name}</td>
                    <td>{section.totalHours.toFixed(1)}h ({section.totalAllHours.toFixed(1)}h)</td>
                    <td>{formatCurrency(section.lon)}</td>
                    <td className="left"><strong>{formatCurrency(section.amount)}</strong></td>
                  </tr>
                ))
              )}
            </tbody>
            {salarySections.length > 0 && (
              <tfoot>
                <tr>
                  <td><strong>Totalt</strong></td>
                  <td><strong>{salaryTotalHours.toFixed(1)}h ({salarySections.reduce((sum, section) => sum + section.totalAllHours, 0).toFixed(1)}h)</strong></td>
                  <td><strong>{formatCurrency(salarySections.reduce((sum, section) => sum + section.lon, 0))}</strong></td>
                  <td className="left"><strong>{formatCurrency(salaryTotalAmount)}</strong></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      ) : viewMode === 'week' && filteredData.length > 0 ? (
        <div className="report-weeks-container">
          {weeklySections.map((section) => (
            <section key={section.key} className="report-week-section">
              <div className="report-week-header">
                <h3>{section.title}</h3>
                <span>{section.totalHours.toFixed(1)}h</span>
              </div>
              <div className="report-table-container">
                <table className={`report-table${selectedCustomer !== 'all' ? ' report-table--with-invoice-select' : ''}`}>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Kund/Projekt</th>
                      <th>Timmar</th>
                      {isOwnerGroupMember && <th>Har fakturerats</th>}
                      {selectedCustomer !== 'all' && <th>Ta med i faktura</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {renderReportRows(section.items, false, false, false, undefined, selectedCustomer !== 'all')}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={2}><strong>Totalt</strong></td>
                      <td className="left"><strong>{section.totalHours.toFixed(1)}h</strong></td>
                      {isOwnerGroupMember && <td></td>}
                      {selectedCustomer !== 'all' && <td></td>}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </section>
          ))}
        </div>
      ) : viewMode === 'year' ? (
        <div className="report-weeks-container">
          {filteredData.length === 0 && !loading ? (
            <div className="report-table-container">
              <table className="report-table">
                <tbody>
                  <tr>
                    <td colSpan={3} className="no-data">Inga tidsregistreringar för detta år</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            yearlySections.map((section) => (
              <section key={section.customer} className="report-week-section">
                <div className="report-week-header">
                  <h3>{section.customer}</h3>
                  <span>{section.totalHours.toFixed(1)}h ({formatPercentage(section.totalHours, yearlyTotalHours)})</span>
                </div>
                <div className="report-table-container">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Projekt</th>
                        <th>Timmar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {renderReportRows(section.items, false, true, true, section.totalHours)}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={2}><strong>Totalt</strong></td>
                        <td className="left"><strong>{section.totalHours.toFixed(1)}h</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </section>
            ))
          )}
        </div>
      ) : isGroupByCustomer ? (
        <div className="report-weeks-container">
          {filteredData.length === 0 && !loading ? (
            <div className="report-table-container">
              <table className="report-table">
                <tbody>
                  <tr>
                    <td colSpan={isOwnerGroupMember ? 4 : 3} className="no-data">Inga tidsregistreringar för denna månad</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            customerSections.map((section) => (
              <section key={section.customer} className="report-week-section">
                <div className="report-week-header">
                  <h3>{section.customer}</h3>
                  <span>{section.totalHours.toFixed(1)}h</span>
                </div>
                <div className="report-table-container">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Projekt</th>
                        <th>Timmar</th>
                        {isOwnerGroupMember && <th>Har fakturerats</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {renderReportRows(section.items, false, true)}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={2}><strong>Totalt</strong></td>
                        <td className="left"><strong>{section.totalHours.toFixed(1)}h</strong></td>
                        {isOwnerGroupMember && <td></td>}
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </section>
            ))
          )}
        </div>
      ) : (
        <div className="report-table-container">
          <table className={`report-table${selectedCustomer !== 'all' ? ' report-table--with-invoice-select' : ''}`}>
            <thead>
              <tr>
                <th></th>
                <th>Kund/Projekt</th>
                <th>Timmar</th>
                {isOwnerGroupMember && <th>Har fakturerats</th>}
                {selectedCustomer !== 'all' && <th>Ta med i faktura</th>}
              </tr>
            </thead>
            <tbody>
              {filteredData.length === 0 && !loading ? (
                <tr>
                  <td colSpan={3 + (isOwnerGroupMember ? 1 : 0) + (selectedCustomer !== 'all' ? 1 : 0)} className="no-data">
                    {selectedCustomer === 'all' 
                      ? 'Inga tidsregistreringar för denna månad'
                      : `Inga tidsregistreringar för ${selectedCustomer}`}
                  </td>
                </tr>
              ) : (
                renderReportRows(filteredData, true, false, false, undefined, selectedCustomer !== 'all')
              )}
            </tbody>
            {filteredData.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={2}><strong>Totalt</strong></td>
                  <td className="left"><strong>{totalHours.toFixed(1)}h</strong></td>
                  <td></td>
                  {selectedCustomer !== 'all' && <td></td>}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
      {showExportDialog && (
        <div className="export-dialog-overlay" onClick={() => setShowExportDialog(false)}>
          <div className="export-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Exportera till Excel</h3>
            <p>Vill du exportera för bara denna månad eller alla månader?</p>
            <div className="export-dialog-buttons">
              <button className="export-dialog-btn" onClick={exportToExcel}>
                📅 Denna månad
              </button>
              <button className="export-dialog-btn" onClick={exportToExcelAllMonths}>
                📋 Alla månader
              </button>
              <button className="export-dialog-btn cancel" onClick={() => setShowExportDialog(false)}>
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}
      {showCreateInvoiceDialog && (
        <div className="export-dialog-overlay" onClick={() => setShowCreateInvoiceDialog(false)}>
          <div className="export-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Skapa faktura</h3>
            <p>Välj hur fakturarader ska grupperas.</p>
            <div className="view-toggle" role="group" aria-label="Välj gruppering för faktura">
              <button
                type="button"
                className={`view-toggle-button ${invoiceGroupingMode === 'project' ? 'active' : ''}`}
                onClick={() => setInvoiceGroupingMode('project')}
                disabled={exportingInvoice}
              >
                Projekt
              </button>
              <button
                type="button"
                className={`view-toggle-button ${invoiceGroupingMode === 'activity' ? 'active' : ''}`}
                onClick={() => setInvoiceGroupingMode('activity')}
                disabled={exportingInvoice}
              >
                Aktivitet
              </button>
            </div>
            <p style={{ marginTop: '10px' }}>
              Om du väljer Aktivitet blir det en rad per aktivitet. Om du väljer Projekt slås rader ihop per projekt.
            </p>
            <div className="export-dialog-buttons">
              <button className="export-button invoice-button" onClick={handleCreateInvoiceConfirm}>
                💵 Skapa faktura
              </button>
              <button className="export-dialog-btn cancel" onClick={() => setShowCreateInvoiceDialog(false)}>
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Report;
